import styled from 'styled-components';
import React, {FC} from 'react';
import {Link} from 'gatsby';

import {Icon} from '../../../molecules/icon';
import {
  palette,
  fontWeights,
  fontSizesMixins,
  mediaBreakpointDown,
  mediaBreakpointUp
} from '../../../../lib/styleVariables';
import {paletteR4, paletteR4Secondary, fontFamiliesR4} from '../../../../lib/styleVariablesR4';
import {hiddenTabletDownStyle, hiddenDesktopUpStyle} from '../../../../lib/responsiveClassNames';
import {SocialMediaLinks, SocialMediaLink} from '../../socialMedia/widget';

interface FooterProduct {
  title: string;
  text: string;
}

interface FooterLink {
  title: string;
  href: string;
}

interface FooterColumn {
  title: string;
  links: Array<FooterLink>;
}

export interface FooterProps {
  product: FooterProduct;
  content: FooterColumn[];
  socialMediaLinks: SocialMediaLink[];
  bottomLinks: FooterLink[];
  copyrightText: string;
}

const StyledFooterWrapper = styled.div`
  background: ${paletteR4.gray.highlight};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledFooterDiv = styled.div`
  align-self: center;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 84px 140px 54px;

  ${mediaBreakpointDown('desktop')} {
    padding: 42px 70px;
  }

  ${mediaBreakpointDown('tablet')} {
    padding: 58px 32px;
  }
`;

const StyledContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  ${mediaBreakpointDown('tablet')} {
    flex-wrap: wrap;
  }
`;

const StyledProductWrapper = styled.div`
  flex: 1;
  display: flex;
  ${mediaBreakpointDown('tablet')} {
    width: 100%;
    min-width: 100%;
    margin-bottom: 32px;
  }
`;

const StyledLogo = styled(Icon)`
  margin-right: 86px;
`;

const StyledProductInfo = styled.div`
  ${hiddenTabletDownStyle}
`;

const StyledFooterTitle = styled.h5`
  font-family: ${fontFamiliesR4.body};
  line-height: 24px;
  color: ${paletteR4Secondary.black.base};
  font-weight: ${fontWeights.semibold};
  font-size: 16px;
  margin: 0 0 16px 0;
`;

const StyledProductText = styled.p`
  ${fontSizesMixins.text2Spread};
  color: ${paletteR4Secondary.black.base};
  letter-spacing: -0.01em;
  margin-top: 0px;
`;

const StyledColumns = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  margin-left: 118px;

  ${mediaBreakpointDown('tablet')} {
    margin-left: 0px;
    justify-content: space-between;
  }
`;

const StyledColumn = styled.div`
  flex: 1;

  &:first-child {
    padding-right: 12px;
  }
`;

const StyledColumnList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const StyledColumnListItem = styled.li`
  margin-bottom: 28px;
  line-height: 24px;

  ${mediaBreakpointDown('tablet')} {
    margin-bottom: 18px;
  }
`;

const StyledLink = styled(Link)`
  color: ${paletteR4Secondary.black.base};
  font-size: 18px;
  text-decoration: none;

  ${mediaBreakpointDown('tablet')} {
    font-size: 15px;
  }
`;

const StyledLineBreak = styled.div`
  margin-top: 56px;
  border-bottom: 1px solid ${paletteR4.gray.base};

  ${hiddenDesktopUpStyle};
`;

const StyledBottomDiv = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 136px;

  ${mediaBreakpointDown('tablet')} {
    flex-direction: column;
    margin-top: 54px;
  }
`;

const StyledSocialMediaLinksWrapper = styled.div`
  color: ${palette.black.base};
  display: flex;
  flex: 1;
  margin-right: 24px;
`;

const socialMediaLinkStyles = `
  display: flex;
  align-items: center;
  margin-right: 18px;

  ${mediaBreakpointUp('desktop')}{
    width: 18px;
    height: 18px;
  }

  ${mediaBreakpointDown('tablet')}{
    &:not(:last-child) {
      margin-right: 48px;
    }
  }
`;

const StyledRightDiv = styled.div`
  display: flex;
  align-items: center;

  ${mediaBreakpointDown('tablet')} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const StyledFooterNav = styled.div`
  display: flex;
  line-height: 24px;

  ${mediaBreakpointDown('tablet')} {
    flex-direction: column;
    align-items: flex-start;
    margin-top: 72px;
  }
`;

const StyledNavLink = styled.a`
  font-weight: ${fontWeights.semibold};
  color: ${paletteR4Secondary.black.base};
  font-size: 11px;
  text-decoration: none;

  &:not(:last-child) {
    margin-right: 38px;
  }

  ${mediaBreakpointDown('tablet')} {
    font-weight: ${fontWeights.normal};
    font-size: 13px;
    margin-right: 0px;

    &:not(:last-child) {
      margin-bottom: 36px;
    }
  }
`;

const StyledCopyright = styled.div`
  color: ${paletteR4Secondary.black.base};
  font-size: 11px;
  text-align: right;
  margin-left: 32px;

  ${mediaBreakpointDown('tablet')} {
    font-size: 13px;
    margin-top: 48px;
    margin-left: 0px;
    text-align: left;
  }
`;

const splitFooterMenu = (arr: FooterColumn[]) => {
  const indexToSplit = Math.floor(arr.length / 2);
  const firstHalf = arr.slice(0, indexToSplit);
  const secondHalf = arr.slice(indexToSplit);

  return [firstHalf, secondHalf];
};

export const Footer: FC<FooterProps> = props => {
  const [footerMenuFirstBatch, footerMenuSecondBatch] = splitFooterMenu(props.content);

  return (
    <StyledFooterWrapper>
      <StyledFooterDiv>
        <StyledContentWrapper>
          <StyledProductWrapper>
            <Link to="/">
              <StyledLogo name="r4HomeLogoSmall" useR4 />
            </Link>
            <StyledProductInfo>
              <StyledFooterTitle>{props.product.title}</StyledFooterTitle>
              <StyledProductText>{props.product.text}</StyledProductText>
            </StyledProductInfo>
          </StyledProductWrapper>
          <StyledColumns>
            {footerMenuFirstBatch.map((column: FooterColumn) => (
              <StyledColumn key={column.title}>
                <StyledFooterTitle>{column.title}</StyledFooterTitle>
                <StyledColumnList>
                  {column.links.map((link: FooterLink) => (
                    <StyledColumnListItem key={link.title}>
                      <StyledLink to={link.href} title={link.title}>
                        {link.title}
                      </StyledLink>
                    </StyledColumnListItem>
                  ))}
                </StyledColumnList>
              </StyledColumn>
            ))}
            {footerMenuSecondBatch.map((column: FooterColumn) => (
              <StyledColumn key={column.title}>
                <StyledFooterTitle>{column.title}</StyledFooterTitle>
                <StyledColumnList>
                  {column.links.map((link: FooterLink) => (
                    <StyledColumnListItem key={link.title}>
                      <StyledLink to={link.href} title={link.title}>
                        {link.title}
                      </StyledLink>
                    </StyledColumnListItem>
                  ))}
                </StyledColumnList>
              </StyledColumn>
            ))}
          </StyledColumns>
        </StyledContentWrapper>
        <StyledLineBreak />
        <StyledBottomDiv>
          <StyledSocialMediaLinksWrapper>
            <SocialMediaLinks
              links={props.socialMediaLinks}
              $css={socialMediaLinkStyles}
              size={28}
              designVersion="v1"
            />
          </StyledSocialMediaLinksWrapper>
          <StyledRightDiv>
            <StyledFooterNav>
              {props.bottomLinks.map(link => (
                <StyledNavLink key={link.title} href={link.href}>
                  {link.title}
                </StyledNavLink>
              ))}
            </StyledFooterNav>
            <StyledCopyright>{props.copyrightText}</StyledCopyright>
          </StyledRightDiv>
        </StyledBottomDiv>
      </StyledFooterDiv>
    </StyledFooterWrapper>
  );
};
